import { Card, CardBody, CardTitle } from 'reactstrap';
import cls from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faToolbox, faTrafficLight, faTruck } from '@fortawesome/free-solid-svg-icons';
import { useFormValues, useSetFieldValue } from '@s20/components/forms/FormContext';
import { Flex } from '../../../../../../common/forms/FormElements';
import { ConfirmationStatus, DraggableResourceTypes } from '../../../../../../dispatch/Dispatch';
import { CancelJobAssignment } from '../../CancelJobAssignment';
import { ReplaceJobAssignment } from '../../ReplaceJobAssignment';
import { INTERPUNCT } from '../../../../../../common/util';
import { getStatusName } from '../common';
import { EquipmentAssignment } from './EquipmentAssignment';
import { StaffAssignment } from './StaffAssignment';

export const Equipment = ({
    assignmentIndex, crewLead, canEdit, dispatchLocationId, ...group
}) => {
    const setFieldValue = useSetFieldValue();

    const {
        assignment, groups, quantity, jobAssignmentId,
        isRental, isRentalPickup, rentalDropoffQuantity, rentalPickupQuantity,
        isVehicle, equipmentTypeAFADTypeId, id, isGeneric,
        description, assignedEmployee, assignedEquipment, jobId,
    } = group;

    const hasAssignment = !!assignment;

    const selectedResourceGroup = useFormValues('selectedResourceGroup');

    if (hasAssignment) {
        const canAssignToEquipment = !!(
            assignment.equipmentType?.requiredEquipment ?? []
        ).length;

        const canAssignToStaff = !canAssignToEquipment && !!(
            assignment.equipmentType?.groups ?? groups ?? []
        ).length;

        const useCombinedDropdown = canAssignToStaff && canAssignToEquipment;

        const assignmentProps = {
            ...assignment,
            canEdit,
            jobAssignmentId,
            groups: assignment.equipmentType?.groups ?? groups,
            jobId,
            assignedEmployee,
            assignedEquipment,
        };

        return (
            <Card
                className={cls(
                    'rounded-lg',
                    getStatusName(assignment),
                )}
            >
                <CardBody className="p-2">
                    <CardTitle className="text-left d-flex flex-row justify-content-between align-items-center mb-0">
                        <div className="d-flex flex-row flex-fill flex-nowrap justify-content-between align-items-center">
                            <span className="d-flex flex-column">
                                <div>
                                    <strong className="text-muted">
                                        {assignment.equipmentType.description}
                                    </strong>

                                    {isRental ? (
                                        <>
                                            {!assignment.equipmentType.isGeneric ? (
                                                <>
                                                    {` ${INTERPUNCT} `}
                                                    {assignment.identifier}

                                                    <div>
                                                        {isRentalPickup ? 'Pickup' : 'Dropoff'}
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    {rentalDropoffQuantity > 0 && (
                                                        <div>
                                                            Dropoff:
                                                            {' '}
                                                            {rentalDropoffQuantity}
                                                        </div>
                                                    )}

                                                    {rentalPickupQuantity > 0 && (
                                                        <div>
                                                            Pickup:
                                                            {' '}
                                                            {rentalPickupQuantity}
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            {` ${INTERPUNCT} `}
                                            {assignment.equipmentType.isGeneric
                                                ? ` ${assignment.identifier} x ( ${group?.quantity} ) `
                                                : ' ' + assignment.identifier
                                            }
                                        </>
                                    )}

                                    {assignment.dispatchLocation.id !== dispatchLocationId && (
                                        <span className="d-block">Dispatched from {assignment.dispatchLocation.name}</span>
                                    )}

                                </div>
                            </span>

                            <div className="assignmentDropdown">
                                {!useCombinedDropdown && canAssignToEquipment && <EquipmentAssignment {...assignmentProps} />}
                                {!useCombinedDropdown && canAssignToStaff && !canAssignToEquipment && <StaffAssignment {...assignmentProps} />}
                            </div>
                        </div>

                        {
                            [ConfirmationStatus.Unfilled, ConfirmationStatus.Scheduled]
                                .includes(assignment.confirmationStatusId) && canEdit && (
                                <ReplaceJobAssignment
                                    jobAssignmentId={jobAssignmentId}
                                    resourceType={DraggableResourceTypes.Equipment}
                                />
                            )
                        }
                        {
                            // job.canEdit is backed by whether or not the crew has submitted a timesheet.
                            // if we have a timesheet for this crew for this day, we cannot remove the assignment.
                            assignment.confirmationStatusId !== ConfirmationStatus.CancelPending && canEdit && (
                                <CancelJobAssignment
                                    jobAssignmentId={jobAssignmentId}
                                    resourceType={DraggableResourceTypes.Equipment}
                                />
                            )
                        }
                    </CardTitle>
                </CardBody>
            </Card>
        );
    }

    let icon = faToolbox;

    if (isVehicle) {
        icon = faTruck;
    } else if (equipmentTypeAFADTypeId > 1) {
        icon = faTrafficLight;
    }

    return (
        <Card
            className={cls('empty-assignment rounded-lg', selectedResourceGroup?.id === id && 'pulse')}
            tag="button"
            type="button"
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();

                setFieldValue('selectedResourceType', DraggableResourceTypes.Equipment);
                setFieldValue('selectedResourceGroup', group);
            }}
        >
            <CardBody className="p-2">
                <CardTitle className="mb-0">
                    <Flex
                        justifyContent="center"
                        alignItems="center"
                        useColumnOnMobile={false}
                    >
                        <FontAwesomeIcon
                            icon={icon}
                            className="mr-1"
                        />

                        {isRental ? (
                            <>
                                {isGeneric ? (
                                    <>
                                        {description}
                                        {' '}


                                        {rentalDropoffQuantity > 0 && `(Dropoff x ${rentalDropoffQuantity}) `}
                                        {rentalPickupQuantity > 0 && `(Pickup x ${rentalPickupQuantity}) `}

                                    </>
                                ) : (
                                    <>
                                        {description}
                                        {' '}

                                        {isRentalPickup ? '(Pickup)' : '(Dropoff)'}
                                    </>
                                )}
                            </>
                        ) : (
                            <>
                                {quantity >= 1 && isGeneric
                                    ? `${description} x ${quantity}`
                                    : description
                                }
                            </>
                        )}
                    </Flex>
                </CardTitle>
            </CardBody>
        </Card>
    );
};